import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MDBBtn, MDBInput, MDBTextArea,
  MDBModal, MDBModalDialog, MDBModalContent,
  MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter,
} from "mdb-react-ui-kit";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import {
  getCatalogPokemon, getCatalogItems, getCatalogBadges, getCatalogCards, getCatalogEstados, getCatalogRegiones,
  adminSavePokemon, adminDeletePokemon,
  adminSaveItem, adminDeleteItem,
  adminSaveBadge, adminDeleteBadge,
  adminSaveCard, adminDeleteCard,
  isOk,
} from "Projects/CHAMP_championsjourney_project/Utils/ChampService";
import CatalogAutocomplete from "Projects/CHAMP_championsjourney_project/Components/CatalogAutocomplete/CatalogAutocomplete";
import { MfmImageUploader } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmImageUploader/MfmImageUploader";
import "./CHAMPAdminPanel.css";

// ─── helpers ─────────────────────────────────────────────────────────────────

function emptyPokemon() {
  return {
    idPokemon: "", nombre: "", tipo1: "", tipo2: "", fase: "", region: "",
    ps: "", ataqueBase: "", criticoDado: "", evolucionaDe: null, metodoEvolucion: "NINGUNO",
    xpEvolucion: "", idObjetoEvolucion: null, descripcionRequisito: "", inicial: false,
    pokedexTexto: "", frontUuid: "", backUuid: "",
    debilidades: "", inmunidades: "", mos: "",
  };
}

function emptyItem() {
  return {
    nombre: "", tipo: "OBJETO", numeroUltra: "", descripcion: "",
    precio: "", utilizable: false, efectoTipo: "", efectoValor: "", idEstadoEfecto: null,
  };
}

function emptyBadge() {
  return { nombre: "", region: "", ciudad: "", lider: "", tipo: "", orden: "" };
}

function emptyCard() {
  return { nombre: "", clase: "", descripcion: "" };
}

// ─── Confirmación de borrado ───────────────────────────────────────────────

function ConfirmDeleteModal({ show, onHide, label, onConfirm, loading }) {
  return (
    <MDBModal show={show} onHide={onHide} tabIndex="-1">
      <MDBModalDialog centered>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>⚠️ Confirmar eliminación</MDBModalTitle>
            <MDBBtn className="btn-close" color="none" onClick={onHide} />
          </MDBModalHeader>
          <MDBModalBody>
            ¿Eliminar <strong>{label}</strong>? Esta acción no se puede deshacer.
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="secondary" onClick={onHide}>Cancelar</MDBBtn>
            <MDBBtn color="danger" disabled={loading} onClick={onConfirm}>
              {loading ? "Eliminando…" : "Eliminar"}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}

// ─── Pestaña Pokémon ──────────────────────────────────────────────────────────

function PokemonTab({ onLoading }) {
  const [list, setList] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [form, setForm] = useState(emptyPokemon());
  const [editId, setEditId] = useState(0); // 0 = nuevo
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const closeDelete = useCallback(() => setDeleteTarget(null), []);
  const [fText, setFText] = useState("");
  const [fTipo, setFTipo] = useState("");
  const [fRegion, setFRegion] = useState("");
  const [fFase, setFFase] = useState("");
  const [fInicial, setFInicial] = useState(false);
  const [regionCatalog, setRegionCatalog] = useState([]);

  const load = useCallback(async () => {
    onLoading && onLoading(true);
    const res = await getCatalogPokemon();
    onLoading && onLoading(false);
    if (isOk(res) && Array.isArray(res.DATA)) setList(res.DATA);
  }, [onLoading]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getCatalogItems().then((res) => {
      if (isOk(res) && Array.isArray(res.DATA)) setItemList(res.DATA);
    });
    getCatalogRegiones().then((res) => {
      if (isOk(res) && Array.isArray(res.DATA)) setRegionCatalog(res.DATA);
    });
  }, []);

  // Opciones para los autocomplete de evolución
  const pokemonOptions = list.map((p) => ({ id: p.idPokemon, label: `#${p.idPokemon} ${p.nombre}` }));
  const itemOptions    = itemList.map((i) => ({ id: i.idObjeto, label: i.nombre }));
  const regionOptions  = regionCatalog; // cargado desde catalog/regiones
  const tipoOptions    = [...new Set(list.flatMap((p) => [p.tipo1, p.tipo2]).filter(Boolean))].sort();
  const faseOptions    = [...new Set(list.map((p) => p.fase).filter((f) => f != null))].sort((a, b) => a - b);
  const filtered = list.filter((p) => {
    const term = fText.trim().toLowerCase();
    if (term && !String(p.idPokemon).includes(term) && !(p.nombre || "").toLowerCase().includes(term)) return false;
    if (fTipo && p.tipo1 !== fTipo && p.tipo2 !== fTipo) return false;
    if (fRegion && p.region !== fRegion) return false;
    if (fFase !== "" && String(p.fase) !== fFase) return false;
    if (fInicial && !p.inicial) return false;
    return true;
  });

  function startEdit(p) {
    setEditId(p.idPokemon);
    setForm({
      idPokemon: p.idPokemon,
      nombre: p.nombre || "",
      tipo1: p.tipo1 || "",
      tipo2: p.tipo2 || "",
      fase: p.fase ?? "",
      region: p.region || "",
      ps: p.ps ?? "",
      ataqueBase: p.ataqueBase || "",
      criticoDado: p.criticoDado || "",
      evolucionaDe: p.evolucionaDe ?? null,
      metodoEvolucion: p.metodoEvolucion || "NINGUNO",
      xpEvolucion: p.xpEvolucion ?? "",
      idObjetoEvolucion: p.idObjetoEvolucion ?? null,
      descripcionRequisito: p.descripcionRequisito || "",
      inicial: p.inicial || false,
      pokedexTexto: p.pokedexTexto || "",
      frontUuid: p.frontUuid || "",
      backUuid: p.backUuid || "",
      debilidades: (p.debilidades || []).join(", "),
      inmunidades: (p.inmunidades || []).join(", "),
      mos: (p.mos || []).join(", "),
    });
  }

  const cancelEdit = useCallback(() => {
    setEditId(0);
    setForm(emptyPokemon());
  }, []);

  function fc(field, value) { setForm((prev) => ({ ...prev, [field]: value })); }

  async function save() {
    if (!form.nombre || !form.idPokemon) {
      toast.show({ message: "ID y nombre son obligatorios", type: "warning" });
      return;
    }
    setSaving(true);
    const id = parseInt(form.idPokemon, 10) || 0;
    const fields = {
      nombre: form.nombre, tipo1: form.tipo1, tipo2: form.tipo2,
      fase: form.fase !== "" ? parseInt(form.fase, 10) : null,
      evolucionaDe: form.evolucionaDe ?? null,
      metodoEvolucion: form.metodoEvolucion,
      xpEvolucion: form.xpEvolucion !== "" ? parseInt(form.xpEvolucion, 10) : null,
      idObjetoEvolucion: form.idObjetoEvolucion ?? null,
      descripcionRequisito: form.descripcionRequisito, inicial: form.inicial,
      ps: form.ps !== "" ? parseInt(form.ps, 10) : null,
      ataqueBase: form.ataqueBase, criticoDado: form.criticoDado, region: form.region,
      pokedexTexto: form.pokedexTexto, frontUuid: form.frontUuid, backUuid: form.backUuid,
      debilidades: form.debilidades, inmunidades: form.inmunidades, mos: form.mos,
    };
    const res = await adminSavePokemon(id, fields);
    setSaving(false);
    if (isOk(res)) {
      toast.show({ message: "Pokémon guardado", type: "success" });
      cancelEdit();
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "Error al guardar", type: "danger" });
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const res = await adminDeletePokemon(deleteTarget.idPokemon);
    setDeleteLoading(false);
    if (isOk(res)) {
      toast.show({ message: "Pokémon eliminado", type: "success" });
      closeDelete();
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "Error al eliminar", type: "danger" });
    }
  }

  return (
    <div className="champ-admin-section">
      <div className="champ-admin-list-header">
        <h3>Catálogo Pokémon ({filtered.length}{filtered.length !== list.length ? ` / ${list.length}` : ""})</h3>
        <MDBBtn size="sm" color="success" onClick={() => { setEditId(-1); setForm(emptyPokemon()); }}>
          + Nuevo Pokémon
        </MDBBtn>
      </div>
      <div className="champ-admin-filter-bar">
        <MDBInput label="🔍 Nombre o #" value={fText} onChange={(e) => setFText(e.target.value)} />
        <select className="champ-admin-filter-select" value={fTipo} onChange={(e) => setFTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          {tipoOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="champ-admin-filter-select" value={fRegion} onChange={(e) => setFRegion(e.target.value)}>
          <option value="">Todas las regiones</option>
          {regionOptions.map((r) => <option key={r.nombre} value={r.nombre}>{r.nombre.charAt(0) + r.nombre.slice(1).toLowerCase()}</option>)}
        </select>
        <select className="champ-admin-filter-select" value={fFase} onChange={(e) => setFFase(e.target.value)}>
          <option value="">Todas las fases</option>
          {faseOptions.map((f) => <option key={f} value={String(f)}>Fase {f}</option>)}
        </select>
        <label className="champ-admin-filter-check">
          <input type="checkbox" checked={fInicial} onChange={(e) => setFInicial(e.target.checked)} />
          Solo iniciales
        </label>
        <button type="button" className="champ-admin-filter-clear"
          onClick={() => { setFText(""); setFTipo(""); setFRegion(""); setFFase(""); setFInicial(false); }}>
          ✕ Limpiar
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="champ-admin-table">
          <thead>
            <tr>
              <th>#</th><th>Nombre</th><th>Tipo 1</th><th>Tipo 2</th>
              <th>Fase</th><th>Región</th><th>PS</th><th className="actions">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="champ-admin-empty">Sin resultados</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.idPokemon}>
                <td>{p.idPokemon}</td>
                <td><strong>{p.nombre}</strong></td>
                <td>{p.tipo1}</td>
                <td>{p.tipo2 || "—"}</td>
                <td>{p.fase ?? "—"}</td>
                <td>{p.region || "—"}</td>
                <td>{p.ps ?? "—"}</td>
                <td className="actions">
                  <MDBBtn size="sm" color="warning" onClick={() => startEdit(p)}>✏️</MDBBtn>
                  <MDBBtn size="sm" color="danger" onClick={() => setDeleteTarget(p)}>🗑️</MDBBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MDBModal show={editId !== 0} onHide={cancelEdit} tabIndex="-1">
        <MDBModalDialog size="xl" scrollable>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{editId > 0 ? `Editando Pokémon #${editId}` : "Nuevo Pokémon"}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={cancelEdit} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="champ-admin-form-grid">
                <MDBInput type="number" label="ID Pokémon *" value={form.idPokemon} onChange={(e) => fc("idPokemon", e.target.value)} disabled={editId > 0} />
                <MDBInput label="Nombre *" value={form.nombre} onChange={(e) => fc("nombre", e.target.value)} />
                <MDBInput label="Tipo 1" value={form.tipo1} onChange={(e) => fc("tipo1", e.target.value)} />
                <MDBInput label="Tipo 2" value={form.tipo2} onChange={(e) => fc("tipo2", e.target.value)} />
                <MDBInput type="number" label="Fase" value={form.fase} onChange={(e) => fc("fase", e.target.value)} />
                <div>
                  <label className="form-label">Región</label>
                  <select className="form-select" value={form.region} onChange={(e) => fc("region", e.target.value)}>
                    <option value="">Sin región</option>
                    {regionCatalog.map((r) => (
                      <option key={r.nombre} value={r.nombre}>{r.nombre.charAt(0) + r.nombre.slice(1).toLowerCase()}</option>
                    ))}
                  </select>
                </div>
                <MDBInput type="number" label="PS base" value={form.ps} onChange={(e) => fc("ps", e.target.value)} />
                <MDBInput label="Ataque base" value={form.ataqueBase} onChange={(e) => fc("ataqueBase", e.target.value)} />
                <MDBInput label="Crítico dado" value={form.criticoDado} onChange={(e) => fc("criticoDado", e.target.value)} />
                <CatalogAutocomplete
                  label="Evoluciona de (Pokémon)"
                  value={form.evolucionaDe}
                  onChange={(id) => fc("evolucionaDe", id)}
                  options={pokemonOptions}
                />
                <MDBInput label="Método evolución" value={form.metodoEvolucion} onChange={(e) => fc("metodoEvolucion", e.target.value)} />
                <MDBInput type="number" label="XP evolución" value={form.xpEvolucion} onChange={(e) => fc("xpEvolucion", e.target.value)} />
                <CatalogAutocomplete
                  label="Objeto evolución"
                  value={form.idObjetoEvolucion}
                  onChange={(id) => fc("idObjetoEvolucion", id)}
                  options={itemOptions}
                />
                <MDBInput label="Descripción requisito" value={form.descripcionRequisito} onChange={(e) => fc("descripcionRequisito", e.target.value)} />
                <MDBInput label="Debilidades (CSV)" value={form.debilidades} onChange={(e) => fc("debilidades", e.target.value)} />
                <MDBInput label="Inmunidades (CSV)" value={form.inmunidades} onChange={(e) => fc("inmunidades", e.target.value)} />
                <MDBInput label="MO (CSV)" value={form.mos} onChange={(e) => fc("mos", e.target.value)} />
                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.88rem" }}>
                  <input type="checkbox" checked={!!form.inicial} onChange={(e) => fc("inicial", e.target.checked)} />
                  Pokémon inicial
                </label>
                <div className="champ-admin-form-full">
                  <MDBTextArea label="Texto Pokédex" rows={3} value={form.pokedexTexto} onChange={(e) => fc("pokedexTexto", e.target.value)} />
                </div>
              </div>
              <div className="champ-admin-img-pickers">
                <div className="champ-admin-img-picker-slot">
                  <p className="champ-admin-img-label">🖼 Frente</p>
                  <MfmImageUploader
                    uuid={form.frontUuid || undefined}
                    appCode="CHAMP"
                    library="pokemon"
                    mediaType="image"
                    defaultVisibility="public"
                    onUpload={(r) => fc("frontUuid", r.uuid)}
                    onDelete={() => fc("frontUuid", "")}
                  />
                </div>
                <div className="champ-admin-img-picker-slot">
                  <p className="champ-admin-img-label">🖼 Reverso</p>
                  <MfmImageUploader
                    uuid={form.backUuid || undefined}
                    appCode="CHAMP"
                    library="pokemon"
                    mediaType="image"
                    defaultVisibility="public"
                    onUpload={(r) => fc("backUuid", r.uuid)}
                    onDelete={() => fc("backUuid", "")}
                  />
                </div>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={cancelEdit}>Cancelar</MDBBtn>
              <MDBBtn color="primary" disabled={saving} onClick={save}>
                {saving ? "Guardando…" : "💾 Guardar"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={closeDelete}
        label={deleteTarget?.nombre}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
}

// ─── Pestaña Objetos ──────────────────────────────────────────────────────────

function ItemsTab({ onLoading }) {
  const [list, setList] = useState([]);
  const [estadoList, setEstadoList] = useState([]);
  const [form, setForm] = useState(emptyItem());
  const [editId, setEditId] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const closeDelete = useCallback(() => setDeleteTarget(null), []);
  const [fTextItems, setFTextItems] = useState("");
  const [fTipoItems, setFTipoItems] = useState("");
  const [fUtilizable, setFUtilizable] = useState(false);

  const load = useCallback(async () => {
    onLoading && onLoading(true);
    const res = await getCatalogItems();
    onLoading && onLoading(false);
    if (isOk(res) && Array.isArray(res.DATA)) setList(res.DATA);
  }, [onLoading]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getCatalogEstados().then((res) => {
      if (isOk(res) && Array.isArray(res.DATA)) setEstadoList(res.DATA);
    });
  }, []);

  const estadoOptions = estadoList.map((e) => ({ id: e.idEstado, label: e.nombre }));
  const tipoOptionsItems = [...new Set(list.map((i) => i.tipo).filter(Boolean))].sort();
  const filteredItems = list.filter((i) => {
    const term = fTextItems.trim().toLowerCase();
    if (term && !(i.nombre || "").toLowerCase().includes(term)) return false;
    if (fTipoItems && i.tipo !== fTipoItems) return false;
    if (fUtilizable && !i.utilizable) return false;
    return true;
  });

  function startEdit(item) {
    setEditId(item.idObjeto);
    setForm({
      nombre: item.nombre || "", tipo: item.tipo || "OBJETO",
      numeroUltra: item.numeroUltra ?? "", descripcion: item.descripcion || "",
      precio: item.precio ?? "", utilizable: item.utilizable || false,
      efectoTipo: item.efectoTipo || "", efectoValor: item.efectoValor ?? "",
      idEstadoEfecto: item.idEstadoEfecto ?? null,
    });
  }

  const cancelEdit = useCallback(() => { setEditId(0); setForm(emptyItem()); }, []);
  function fc(f, v) { setForm((p) => ({ ...p, [f]: v })); }

  async function save() {
    if (!form.nombre || !form.tipo) {
      toast.show({ message: "Nombre y tipo son obligatorios", type: "warning" });
      return;
    }
    setSaving(true);
    const fields = {
      nombre: form.nombre, tipo: form.tipo,
      numeroUltra: form.numeroUltra !== "" ? parseInt(form.numeroUltra, 10) : null,
      descripcion: form.descripcion,
      precio: form.precio !== "" ? parseInt(form.precio, 10) : null,
      utilizable: form.utilizable,
      efectoTipo: form.efectoTipo || null,
      efectoValor: form.efectoValor !== "" ? parseInt(form.efectoValor, 10) : null,
      idEstadoEfecto: form.idEstadoEfecto ?? null,
    };
    const res = await adminSaveItem(editId > 0 ? editId : 0, fields);
    setSaving(false);
    if (isOk(res)) {
      toast.show({ message: "Objeto guardado", type: "success" });
      cancelEdit();
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "Error al guardar", type: "danger" });
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const res = await adminDeleteItem(deleteTarget.idObjeto);
    setDeleteLoading(false);
    if (isOk(res)) {
      toast.show({ message: "Objeto eliminado", type: "success" });
      closeDelete();
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "Error al eliminar", type: "danger" });
    }
  }

  return (
    <div className="champ-admin-section">
      <div className="champ-admin-list-header">
        <h3>Objetos ({filteredItems.length}{filteredItems.length !== list.length ? ` / ${list.length}` : ""})</h3>
        <MDBBtn size="sm" color="success" onClick={() => { setEditId(-1); setForm(emptyItem()); }}>
          + Nuevo objeto
        </MDBBtn>
      </div>
      <div className="champ-admin-filter-bar">
        <MDBInput label="🔍 Buscar nombre" value={fTextItems} onChange={(e) => setFTextItems(e.target.value)} />
        <select className="champ-admin-filter-select" value={fTipoItems} onChange={(e) => setFTipoItems(e.target.value)}>
          <option value="">Todos los tipos</option>
          {tipoOptionsItems.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="champ-admin-filter-check">
          <input type="checkbox" checked={fUtilizable} onChange={(e) => setFUtilizable(e.target.checked)} />
          Solo utilizables
        </label>
        <button type="button" className="champ-admin-filter-clear"
          onClick={() => { setFTextItems(""); setFTipoItems(""); setFUtilizable(false); }}>
          ✕ Limpiar
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="champ-admin-table">
          <thead>
            <tr>
              <th>#</th><th>Nombre</th><th>Tipo</th><th>Precio</th>
              <th>Efecto tipo</th><th>Efecto valor</th><th>Utilizable</th><th className="actions">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 && (
              <tr><td colSpan={8} className="champ-admin-empty">Sin resultados</td></tr>
            )}
            {filteredItems.map((item) => (
              <tr key={item.idObjeto}>
                <td>{item.idObjeto}</td>
                <td><strong>{item.nombre}</strong></td>
                <td>{item.tipo}</td>
                <td>{item.precio ?? "—"}</td>
                <td>{item.efectoTipo || "—"}</td>
                <td>{item.efectoValor ?? "—"}</td>
                <td>{item.utilizable ? "✔" : "—"}</td>
                <td className="actions">
                  <MDBBtn size="sm" color="warning" onClick={() => startEdit(item)}>✏️</MDBBtn>
                  <MDBBtn size="sm" color="danger" onClick={() => setDeleteTarget(item)}>🗑️</MDBBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MDBModal show={editId !== 0} onHide={cancelEdit} tabIndex="-1">
        <MDBModalDialog size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{editId > 0 ? `Editando objeto #${editId}` : "Nuevo objeto"}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={cancelEdit} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="champ-admin-form-grid">
                <MDBInput label="Nombre *" value={form.nombre} onChange={(e) => fc("nombre", e.target.value)} />
                <MDBInput label="Tipo *" value={form.tipo} onChange={(e) => fc("tipo", e.target.value)} />
                <MDBInput type="number" label="Nº Ultra" value={form.numeroUltra} onChange={(e) => fc("numeroUltra", e.target.value)} />
                <MDBInput type="number" label="Precio" value={form.precio} onChange={(e) => fc("precio", e.target.value)} />
                <MDBInput label="Efecto tipo" value={form.efectoTipo} onChange={(e) => fc("efectoTipo", e.target.value)} />
                <MDBInput type="number" label="Efecto valor" value={form.efectoValor} onChange={(e) => fc("efectoValor", e.target.value)} />
                <CatalogAutocomplete
                  label="Estado efecto"
                  value={form.idEstadoEfecto}
                  onChange={(id) => fc("idEstadoEfecto", id)}
                  options={estadoOptions}
                />
                <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.88rem" }}>
                  <input type="checkbox" checked={!!form.utilizable} onChange={(e) => fc("utilizable", e.target.checked)} />
                  Utilizable
                </label>
                <div className="champ-admin-form-full">
                  <MDBTextArea label="Descripción" rows={2} value={form.descripcion} onChange={(e) => fc("descripcion", e.target.value)} />
                </div>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={cancelEdit}>Cancelar</MDBBtn>
              <MDBBtn color="primary" disabled={saving} onClick={save}>
                {saving ? "Guardando…" : "💾 Guardar"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={closeDelete}
        label={deleteTarget?.nombre}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
}

// ─── Pestaña Medallas ─────────────────────────────────────────────────────────

function BadgesTab({ onLoading }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyBadge());
  const [editId, setEditId] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const closeDelete = useCallback(() => setDeleteTarget(null), []);
  const [fTextBadges, setFTextBadges] = useState("");
  const [fRegionBadges, setFRegionBadges] = useState("");
  const [fTipoBadges, setFTipoBadges] = useState("");
  const [regionCatalogBadges, setRegionCatalogBadges] = useState([]);

  const load = useCallback(async () => {
    onLoading && onLoading(true);
    const res = await getCatalogBadges();
    onLoading && onLoading(false);
    if (isOk(res) && Array.isArray(res.DATA)) setList(res.DATA);
  }, [onLoading]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getCatalogRegiones().then((res) => {
      if (isOk(res) && Array.isArray(res.DATA)) setRegionCatalogBadges(res.DATA);
    });
  }, []);

  const regionOptionsBadges = regionCatalogBadges; // cargado desde catalog/regiones
  const tipoOptionsBadges   = [...new Set(list.map((m) => m.tipo).filter(Boolean))].sort();
  const filteredBadges = list.filter((m) => {
    const term = fTextBadges.trim().toLowerCase();
    if (term && !(m.nombre || "").toLowerCase().includes(term)
             && !(m.lider || "").toLowerCase().includes(term)
             && !(m.ciudad || "").toLowerCase().includes(term)) return false;
    if (fRegionBadges && m.region !== fRegionBadges) return false;
    if (fTipoBadges && m.tipo !== fTipoBadges) return false;
    return true;
  });

  function startEdit(m) {
    setEditId(m.idMedalla);
    setForm({
      nombre: m.nombre || "", region: m.region || "",
      ciudad: m.ciudad || "", lider: m.lider || "",
      tipo: m.tipo || "", orden: m.orden ?? "",
    });
  }

  const cancelEdit = useCallback(() => { setEditId(0); setForm(emptyBadge()); }, []);
  function fc(f, v) { setForm((p) => ({ ...p, [f]: v })); }

  async function save() {
    if (!form.nombre || !form.region) {
      toast.show({ message: "Nombre y región son obligatorios", type: "warning" });
      return;
    }
    setSaving(true);
    const fields = {
      nombre: form.nombre, region: form.region, ciudad: form.ciudad,
      lider: form.lider, tipo: form.tipo,
      orden: form.orden !== "" ? parseInt(form.orden, 10) : null,
    };
    const res = await adminSaveBadge(editId > 0 ? editId : 0, fields);
    setSaving(false);
    if (isOk(res)) {
      toast.show({ message: "Medalla guardada", type: "success" });
      cancelEdit();
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "Error al guardar", type: "danger" });
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const res = await adminDeleteBadge(deleteTarget.idMedalla);
    setDeleteLoading(false);
    if (isOk(res)) {
      toast.show({ message: "Medalla eliminada", type: "success" });
      closeDelete();
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "Error al eliminar", type: "danger" });
    }
  }

  return (
    <div className="champ-admin-section">
      <div className="champ-admin-list-header">
        <h3>Medallas ({filteredBadges.length}{filteredBadges.length !== list.length ? ` / ${list.length}` : ""})</h3>
        <MDBBtn size="sm" color="success" onClick={() => { setEditId(-1); setForm(emptyBadge()); }}>
          + Nueva medalla
        </MDBBtn>
      </div>
      <div className="champ-admin-filter-bar">
        <MDBInput label="🔍 Nombre, ciudad o líder" value={fTextBadges} onChange={(e) => setFTextBadges(e.target.value)} />
        <select className="champ-admin-filter-select" value={fRegionBadges} onChange={(e) => setFRegionBadges(e.target.value)}>
          <option value="">Todas las regiones</option>
          {regionOptionsBadges.map((r) => <option key={r.nombre} value={r.nombre}>{r.nombre.charAt(0) + r.nombre.slice(1).toLowerCase()}</option>)}
        </select>
        <select className="champ-admin-filter-select" value={fTipoBadges} onChange={(e) => setFTipoBadges(e.target.value)}>
          <option value="">Todos los tipos</option>
          {tipoOptionsBadges.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="button" className="champ-admin-filter-clear"
          onClick={() => { setFTextBadges(""); setFRegionBadges(""); setFTipoBadges(""); }}>
          ✕ Limpiar
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="champ-admin-table">
          <thead>
            <tr>
              <th>#</th><th>Nombre</th><th>Región</th><th>Ciudad</th>
              <th>Líder</th><th>Tipo</th><th>Orden</th><th className="actions">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {filteredBadges.length === 0 && (
              <tr><td colSpan={8} className="champ-admin-empty">Sin resultados</td></tr>
            )}
            {filteredBadges.map((m) => (
              <tr key={m.idMedalla}>
                <td>{m.idMedalla}</td>
                <td><strong>{m.nombre}</strong></td>
                <td>{m.region}</td>
                <td>{m.ciudad || "—"}</td>
                <td>{m.lider || "—"}</td>
                <td>{m.tipo || "—"}</td>
                <td>{m.orden ?? "—"}</td>
                <td className="actions">
                  <MDBBtn size="sm" color="warning" onClick={() => startEdit(m)}>✏️</MDBBtn>
                  <MDBBtn size="sm" color="danger" onClick={() => setDeleteTarget(m)}>🗑️</MDBBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MDBModal show={editId !== 0} onHide={cancelEdit} tabIndex="-1">
        <MDBModalDialog size="md">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{editId > 0 ? `Editando medalla #${editId}` : "Nueva medalla"}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={cancelEdit} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="champ-admin-form-grid">
                <MDBInput label="Nombre *" value={form.nombre} onChange={(e) => fc("nombre", e.target.value)} />
                <div>
                  <label className="form-label">Región *</label>
                  <select className="form-select" value={form.region} onChange={(e) => fc("region", e.target.value)}>
                    <option value="">Seleccionar región</option>
                    {regionCatalogBadges.map((r) => (
                      <option key={r.nombre} value={r.nombre}>{r.nombre.charAt(0) + r.nombre.slice(1).toLowerCase()}</option>
                    ))}
                  </select>
                </div>
                <MDBInput label="Ciudad" value={form.ciudad} onChange={(e) => fc("ciudad", e.target.value)} />
                <MDBInput label="Líder" value={form.lider} onChange={(e) => fc("lider", e.target.value)} />
                <MDBInput label="Tipo" value={form.tipo} onChange={(e) => fc("tipo", e.target.value)} />
                <MDBInput type="number" label="Orden" value={form.orden} onChange={(e) => fc("orden", e.target.value)} />
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={cancelEdit}>Cancelar</MDBBtn>
              <MDBBtn color="primary" disabled={saving} onClick={save}>
                {saving ? "Guardando…" : "💾 Guardar"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={closeDelete}
        label={deleteTarget?.nombre}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
}

// ─── Pestaña Tarjetas ─────────────────────────────────────────────────────────

function CardsTab({ onLoading }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyCard());
  const [editId, setEditId] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const closeDelete = useCallback(() => setDeleteTarget(null), []);
  const [fTextCards, setFTextCards] = useState("");
  const [fClase, setFClase] = useState("");

  const load = useCallback(async () => {
    onLoading && onLoading(true);
    const res = await getCatalogCards();
    onLoading && onLoading(false);
    if (isOk(res) && Array.isArray(res.DATA)) setList(res.DATA);
  }, [onLoading]);

  useEffect(() => { load(); }, [load]);

  const claseOptions = [...new Set(list.map((t) => t.clase).filter(Boolean))].sort();
  const filteredCards = list.filter((t) => {
    const term = fTextCards.trim().toLowerCase();
    if (term && !(t.nombre || "").toLowerCase().includes(term)
             && !(t.descripcion || "").toLowerCase().includes(term)) return false;
    if (fClase && t.clase !== fClase) return false;
    return true;
  });

  function startEdit(t) {
    setEditId(t.idTarjeta);
    setForm({ nombre: t.nombre || "", clase: t.clase || "", descripcion: t.descripcion || "" });
  }

  const cancelEdit = useCallback(() => { setEditId(0); setForm(emptyCard()); }, []);
  function fc(f, v) { setForm((p) => ({ ...p, [f]: v })); }

  async function save() {
    if (!form.nombre || !form.clase) {
      toast.show({ message: "Nombre y clase son obligatorios", type: "warning" });
      return;
    }
    setSaving(true);
    const res = await adminSaveCard(editId > 0 ? editId : 0, form);
    setSaving(false);
    if (isOk(res)) {
      toast.show({ message: "Tarjeta guardada", type: "success" });
      cancelEdit();
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "Error al guardar", type: "danger" });
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const res = await adminDeleteCard(deleteTarget.idTarjeta);
    setDeleteLoading(false);
    if (isOk(res)) {
      toast.show({ message: "Tarjeta eliminada", type: "success" });
      closeDelete();
      load();
    } else {
      toast.show({ message: res?.MESSAGE || "Error al eliminar", type: "danger" });
    }
  }

  return (
    <div className="champ-admin-section">
      <div className="champ-admin-list-header">
        <h3>Tarjetas Journey ({filteredCards.length}{filteredCards.length !== list.length ? ` / ${list.length}` : ""})</h3>
        <MDBBtn size="sm" color="success" onClick={() => { setEditId(-1); setForm(emptyCard()); }}>
          + Nueva tarjeta
        </MDBBtn>
      </div>
      <div className="champ-admin-filter-bar">
        <MDBInput label="🔍 Nombre o descripción" value={fTextCards} onChange={(e) => setFTextCards(e.target.value)} />
        <select className="champ-admin-filter-select" value={fClase} onChange={(e) => setFClase(e.target.value)}>
          <option value="">Todas las clases</option>
          {claseOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="button" className="champ-admin-filter-clear"
          onClick={() => { setFTextCards(""); setFClase(""); }}>
          ✕ Limpiar
        </button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="champ-admin-table">
          <thead>
            <tr>
              <th>#</th><th>Nombre</th><th>Clase</th><th>Descripción</th><th className="actions">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {filteredCards.length === 0 && (
              <tr><td colSpan={5} className="champ-admin-empty">Sin resultados</td></tr>
            )}
            {filteredCards.map((t) => (
              <tr key={t.idTarjeta}>
                <td>{t.idTarjeta}</td>
                <td><strong>{t.nombre}</strong></td>
                <td>{t.clase}</td>
                <td style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.descripcion || "—"}
                </td>
                <td className="actions">
                  <MDBBtn size="sm" color="warning" onClick={() => startEdit(t)}>✏️</MDBBtn>
                  <MDBBtn size="sm" color="danger" onClick={() => setDeleteTarget(t)}>🗑️</MDBBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MDBModal show={editId !== 0} onHide={cancelEdit} tabIndex="-1">
        <MDBModalDialog size="md">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{editId > 0 ? `Editando tarjeta #${editId}` : "Nueva tarjeta"}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={cancelEdit} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="champ-admin-form-grid">
                <MDBInput label="Nombre *" value={form.nombre} onChange={(e) => fc("nombre", e.target.value)} />
                <MDBInput label="Clase *" value={form.clase} onChange={(e) => fc("clase", e.target.value)} />
                <div className="champ-admin-form-full">
                  <MDBTextArea label="Descripción" rows={3} value={form.descripcion} onChange={(e) => fc("descripcion", e.target.value)} />
                </div>
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={cancelEdit}>Cancelar</MDBBtn>
              <MDBBtn color="primary" disabled={saving} onClick={save}>
                {saving ? "Guardando…" : "💾 Guardar"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={closeDelete}
        label={deleteTarget?.nombre}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </div>
  );
}

// ─── Panel principal ──────────────────────────────────────────────────────────

const TABS = [
  { key: "pokemon", label: "🐣 Pokémon" },
  { key: "items",   label: "🎒 Objetos" },
  { key: "badges",  label: "🏅 Medallas" },
  { key: "cards",   label: "🃏 Tarjetas" },
];

export default function CHAMPAdminPanel({ onLoading }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pokemon");

  return (
    <div className="champ-admin-panel">
      <div className="champ-admin-panel-header">
        <button type="button" className="champ-admin-back-btn" onClick={() => navigate("/champ")}>
          ← Volver
        </button>
        <h2>⚙️ Administración del catálogo</h2>
      </div>
      <div className="champ-admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`champ-admin-tab${activeTab === t.key ? " active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === "pokemon" && <PokemonTab onLoading={onLoading} />}
      {activeTab === "items"   && <ItemsTab   onLoading={onLoading} />}
      {activeTab === "badges"  && <BadgesTab  onLoading={onLoading} />}
      {activeTab === "cards"   && <CardsTab   onLoading={onLoading} />}
    </div>
  );
}
